/**
 * @file        src/layout/TopNav.jsx
 * @description Beyond Destination top navigation bar — the demo's header lockup.
 *              Two variants:
 *                • hero  — transparent, sits over the gradient hero on Home
 *                          (white text, translucent menu pill).
 *                • solid — sticky dark (#0C1218) bar for every inner page.
 *              Reuses the booking store for auth (open the auth modal / go to
 *              profile), the wallet chip, and the Flights/Hotels/Holidays search
 *              entry points — the same handlers the old Sidebar exposed. Purely
 *              chrome: no API, route-guard or business logic changes.
 * @author      Kinetika Labs
 * @created     2026-07-22
 * @changelog
 *   - 2026-10-23 PG — NAV_LOGO_SIZE now imported from BrandLogo (unchanged value).
 *   - 2026-10-08 Claude — Brand logo DOUBLED (h-10 sm:h-14 → h-20 sm:h-28, nav
 *                          only), and the WHOLE bar is now solid white, full width,
 *                          in both variants and both themes (client request) — the
 *                          dark photo bar and the transparent hero bar are gone, so
 *                          the logo needs no badge. Every control on the bar moved
 *                          to fixed dark ink (active pill + auth pill are dark with
 *                          white text; .nav-cta is no longer used here). The bar grew
 *                          to fit the logo: 92px on a phone, 124px from sm, via
 *                          --nav-h, which the row now reads directly. Home hero top
 *                          padding raised to match. Mobile dropdown stays dark.
 *                          Centre menu keeps its pill shape with a 1px black outline
 *                          and thin 1px black dividers between items (hidden beside
 *                          the active pill).
 *   - 2026-09-16 Claude — CORPORATE BADGE in the right-hand actions (and in the
 *                          phone menu, which the bar's `hidden lg:` hides it from).
 *                          Nothing on this bar distinguished a corporate account
 *                          from a personal one, while the wallet pill beside it
 *                          was already showing a POOLED company balance as though
 *                          it were the customer's own money. Shows the company
 *                          name and links to /wallet, where the pooled balance and
 *                          the employee panel live. NOTE: `agencyName` is empty on
 *                          every live session today — no auth endpoint returns
 *                          `agency_name` yet (see the store's doLogin) — so the
 *                          badge falls back to "Corporate account" until the
 *                          backend sends it. The fallback is deliberately not a
 *                          name: inventing one is what made the Sidebar's old
 *                          "Company" read as real.
 *   - 2026-08-07 Claude — Removed the "Contact" pill (client request) from BOTH the
 *                          desktop menu and the mobile dropdown — they are the same
 *                          nav item rendered twice, so dropping only one would show
 *                          Contact on a phone but not on a desktop. This reverses
 *                          the 2026-08-06 addition below; the /contact ROUTE and
 *                          page are untouched and the SiteFooter still links there.
 *   - 2026-08-07 Claude — MOBILE: the wallet chip and the Light/Dark toggle are
 *                          both `hidden sm:inline-flex` on the bar (below 640px the
 *                          row only fits logo + auth pill + menu button), and
 *                          nothing else exposed them — so on a phone a signed-in
 *                          customer could not see their balance and NOBODY could
 *                          switch theme. Both now also render inside the mobile
 *                          dropdown, gated `sm:hidden` so they cannot appear twice
 *                          in the 640–767px band where the bar and the dropdown
 *                          both exist.
 *   - 2026-08-04 Claude — The auth pill follows the theme: it was `bg-white` in
 *                          both, so switching the site to dark left it the one
 *                          bright white object on the bar. Now `.nav-cta`
 *                          (styles/global.css) — white in light, Jet Ink + hairline
 *                          in dark. Applied to "My account" too: it is the same
 *                          control in the same slot once signed in, and leaving it
 *                          white would make the slot change colour with login state.
 *                          The active nav pill (Flights/Hotels/…) is deliberately
 *                          NOT changed — it is a selection indicator, not a button.
 *   - 2026-08-04 Claude — "Log in" + "Sign up" collapsed into ONE white pill,
 *                          "Log in / Create account" (shortened to "Log in"
 *                          below lg, where the full wording overflows the row).
 *                          Both buttons already called the same handler and opened
 *                          the same OTP modal — there is no separate signup flow —
 *                          so the pair only implied a choice the app never had. The
 *                          pill and the ThemeToggle are both `shrink-0` now so the
 *                          longer label can never crowd or ride over the toggle.
 *   - 2026-08-03 Claude — MOBILE: "Log in" is no longer hidden below sm — a returning
 *                          customer on a phone could only see "Sign up". It renders as
 *                          a borderless text button there, and the brand logo (56→40px)
 *                          + row padding/gap were trimmed to make the four-item row fit
 *                          a 375px viewport. Every override resets at sm; desktop is
 *                          pixel-identical.
 *   - 2026-08-03 Claude — Active pill now tracks the home search tab through the store
 *                          (`homeSearchTab`) instead of `location.state.searchTab`.
 *                          Home consumes that state and replaces it with null on
 *                          arrival, so clicking Hotels lit the pill for one render and
 *                          then snapped back to Flights. Also syncs the other way: the
 *                          search card's own tab strip now moves the nav pill.
 *   - 2026-07-29 Claude — Dropped the static "₹ INR · EN" label from the right actions.
 *                          That slot now belongs to the wallet pill, which already sat
 *                          next to it and renders the live balance once `loggedIn` — so
 *                          the bar reads empty→balance across the OTP sign-in instead of
 *                          carrying a label that was never interactive.
 *   - 2026-07-29 Claude — Inner-page brand logo enlarged h-9 sm:h-10 → h-12 sm:h-14.
 *                          The white badge adds py-1 (8px), so the badge grows to
 *                          64px inside the fixed h-[74px] bar — 5px clearance top and
 *                          bottom, so it cannot spill onto the cream section below.
 *                          The hero size is untouched: at h-16 its badge is already
 *                          72px of the 74px bar and has no room left.
 *   - 2026-07-27 Claude — Solid inner-page bar shows the /page-hero.png photo behind a
 *                          dark scrim instead of a flat #0C1218 black (colour kept as
 *                          the load fallback). Hero variant unchanged.
 *   - 2026-07-24 Claude — Active nav pill (Flights/Hotels/…) and the "Sign up" button:
 *                          coral → white with dark ink text, per client request. Same
 *                          swap in the mobile dropdown's active item.
 *   - 2026-07-24 Claude — White-pill text pinned to a fixed #101820 (was text-ink) on
 *                          the active tab, Sign up, My account and the mobile active
 *                          item: text-ink flips off-white in dark mode, so it was
 *                          invisible on the always-white pill. Now dark in both themes.
 *   - 2026-08-06 Claude — Added a "Contact" pill (desktop menu + mobile
 *                          dropdown). /contact previously had no link anywhere
 *                          in this bar — only the SiteFooter (not mounted on
 *                          Packages/PackageDetail, where a visitor most wants
 *                          it) linked there, so the page was effectively
 *                          unreachable from the pages that would send someone
 *                          looking for it.
 *   - 2026-07-22 Claude — Initial. Replaces the left Sidebar with the demo's
 *                          top nav (see layout/AppLayout.jsx).
 *   - 2026-07-23 Claude — Enlarged the brand logo on the Home hero (h-14 sm:h-16)
 *                          while keeping inner pages at the compact size.
 *   - 2026-07-23 Claude — Active nav pill is now coral in the hero too (was white),
 *                          so Flights/Hotels match Holidays/Insurance when selected.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';
import BrandLogo, { NAV_LOGO_SIZE } from '../components/BrandLogo.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';

const LINKS = [
  { key: 'flights', label: 'Flights' },
  { key: 'hotels', label: 'Hotels' },
  { key: 'holidays', label: 'Holidays' },
  { key: 'insurance', label: 'Insurance' },
];

export default function TopNav({ variant = 'solid' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useBooking((s) => s.loggedIn);
  const walletBalance = useBooking((s) => s.walletBalance);
  // Tenant-wide wallet kill-switch (admin -> Wallet Management -> Wallet
  // Settings). Off = every wallet surface is hidden, this bar included.
  const walletFeatureEnabled = useBooking((s) => s.walletFeatureEnabled) !== false;
  const homeSearchTab = useBooking((s) => s.homeSearchTab);
  // Corporate identity. This bar carried NO signal at all that you were signed in
  // to a company account rather than a personal one — the wallet pill showed a
  // pooled company balance as if it were your own money, and "My account" read
  // identically either way. The Sidebar has had this block since it was built;
  // the top bar is the surface most Beyond pages actually render.
  const role = useBooking((s) => s.role);
  const agencyName = useBooking((s) => s.agencyName);
  const isAgent = role === 'b2b_owner' || role === 'b2b_user';
  const [menuOpen, setMenuOpen] = useState(false);

  const hero = variant === 'hero';
  // On Home the selected tab lives in the store, NOT in location.state: Home
  // consumes `state.searchTab` and immediately replaces the entry with state:null
  // (so a refresh starts blank), which used to wipe the highlight back to Flights
  // the instant you clicked Hotels. homeSearchTab survives that and is also written
  // by the search card's own tab strip, so both stay in sync either way.
  const activeKey = location.pathname.startsWith('/hotels') ? 'hotels'
    : location.pathname.startsWith('/packages') ? 'holidays'
      : location.pathname.startsWith('/insurance') ? 'insurance'
        : location.pathname.startsWith('/flights') ? 'flights'
          : (location.state?.searchTab || homeSearchTab || 'flights');

  const goSearch = (key) => {
    setMenuOpen(false);
    if (key === 'hotels') {
      useBooking.getState().set({ homeSearchTab: 'hotels' });
      navigate('/', { state: { searchTab: 'hotels' } });
    } else if (key === 'flights') {
      useBooking.getState().resetFlightSearch?.();
      useBooking.getState().set({ homeSearchTab: 'flights' });
      navigate('/', { state: { searchTab: 'flights' } });
    } else if (key === 'holidays') navigate('/packages');
    else if (key === 'insurance') navigate('/insurance');
  };

  const authClick = () => {
    setMenuOpen(false);
    if (loggedIn) navigate('/profile');
    else window.dispatchEvent(new CustomEvent('nh:open-auth'));
  };

  // Colour sets differ per variant so the same markup reads on the gradient hero
  // (light-on-dark, transparent) and on the solid inner bar.
  const linkBase = 'px-4 py-2 rounded-full text-[13.5px] font-semibold cursor-pointer transition-colors';
  // Active nav pill is a WHITE pill with dark text in BOTH variants so the selected
  // item reads the same whether you're on the home hero (Flights/Hotels) or an inner
  // page (Holidays/Insurance). White (not coral) per client request. Text is a FIXED
  // dark (#101820), NOT the theme `text-ink` token: the pill is white in both themes,
  // but text-ink flips to off-white in dark mode and vanished on the white fill.
  // The bar is WHITE in both variants and both themes (client request
  // 2026-10-08), so every colour on it is a fixed dark ink, never a theme token:
  // the active pill inverts to dark-with-white-text, since a white pill on a
  // white bar would vanish.
  const linkOn = 'bg-[#101820] text-white';
  const linkOff = 'text-[#101820]/75 hover:bg-[#101820]/10';

  return (
    <header
      className={
        (hero ? 'absolute top-0 inset-x-0 z-30' : 'sticky top-0 z-40')
        + ' bg-white border-b border-[#101820]/10 shadow-sm'
      }
    >
      {/* px-4 on mobile (was px-5) — kept from when this row carried two auth
          buttons. It is now one, so the 8px is headroom rather than a necessity. */}
      {/* Row height is --nav-h (styles/global.css), the same value every sticky
          sub-header offsets by, so the two cannot drift apart. */}
      <div className="max-w-[1240px] mx-auto px-4 sm:px-8 h-[var(--nav-h)] flex items-center justify-between gap-2 sm:gap-4">
        {/* Brand — sits straight on the white bar; no badge needed any more. */}
        <button onClick={() => { setMenuOpen(false); navigate('/', { state: { searchTab: 'flights' } }); }} className="shrink-0 flex items-center cursor-pointer" aria-label="Beyond Destination home">
          <span className="inline-flex items-center">
            {/* Both variants share one size, so the logo does not change size
                when you move off the home page. Twice LOGO_SIZE by client request
                — nav and the legal pages; LOGO_SIZE still sizes modals and vouchers. */}
            <BrandLogo
              className={NAV_LOGO_SIZE}
              colorClassName="text-ink"
              showTagline={false}
            />
          </span>
        </button>

        {/* Center menu (md+) — the original pill shape, with a black outline and
            thin black dividers between items (client request). A divider is
            dropped beside the active pill, where it would butt into the fill. */}
        <nav className="hidden md:flex items-center gap-1 rounded-full p-1.5 bg-white border border-[#101820]">
          {LINKS.map((l, i) => {
            const prev = LINKS[i - 1];
            const showDivider = i > 0 && activeKey !== l.key && activeKey !== prev.key;
            return (
              <React.Fragment key={l.key}>
                {i > 0 && (
                  <span aria-hidden="true" className={'w-px h-4 ' + (showDivider ? 'bg-[#101820]' : 'bg-transparent')} />
                )}
                <a
                  onClick={() => goSearch(l.key)}
                  className={linkBase + ' ' + (activeKey === l.key ? linkOn : linkOff)}
                >
                  {l.label}
                </a>
              </React.Fragment>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2.5 sm:gap-3.5 shrink-0">
          {/* The static "₹ INR · EN" label is gone — this slot is the wallet's. It
              stays empty until sign-in, then holds the live balance. */}
          {loggedIn && walletFeatureEnabled && (
            <button
              onClick={() => navigate('/wallet')}
              title="My wallet"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#101820]/5 border border-[#101820]/15 text-[#101820] text-xs font-bold hover:bg-[#101820]/10 cursor-pointer"
            >
              {inr(walletBalance || 0)}
            </button>
          )}
          {/* shrink-0 so the auth button next to it can never squash or ride over
              the toggle when the row runs short of width. */}
          <ThemeToggle variant="sky" size="sm" showLabel={false} className="hidden sm:inline-flex shrink-0" />
          {loggedIn ? (
            <button onClick={authClick} className="bg-[#101820] text-white hover:bg-[#101820]/85 px-5 py-2.5 rounded-full text-[13.5px] font-bold transition cursor-pointer">
              My account
            </button>
          ) : (
            /* ONE button for both. "Log in" and "Sign up" were always the same
               handler opening the same modal — there is no separate B2C signup
               endpoint — so two pills advertised a choice that did not exist.
               `shrink-0 whitespace-nowrap` is load-bearing: it keeps this button
               from being squeezed into, or wrapping over, the ThemeToggle beside
               it, which is the one collision this row can produce (both are plain
               flex siblings, so nothing here can overlap unless the row overflows).
               The label shortens below lg for the same reason — at md the full
               wording plus the brand badge and the centre nav overflow a 768px
               row, which is what would push the toggle off the bar. */
            <button
              onClick={authClick}
              className="bg-[#101820] text-white hover:bg-[#101820]/85 shrink-0 whitespace-nowrap px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-[13px] sm:text-[13.5px] font-bold transition cursor-pointer"
            >
              <span className="lg:hidden">Log in</span>
              <span className="hidden lg:inline">Log in / Create account</span>
            </button>
          )}
          {/* Corporate badge — the slot to the RIGHT of "My account". A corporate
              buyer books on behalf of their company and spends a POOLED balance,
              so which account they are acting under has to be on screen; nothing
              in this bar said so. Goes to /wallet, which is where the pooled
              balance and (for an owner) the employee panel live.

              WIDTH — a company name is arbitrary user text, so it is capped and
              truncated rather than allowed to push the auth pill off the row.
              `hidden lg:inline-flex` keeps it off the md bar, which is already
              tight with the logo, the centre nav and the auth pill; the phone
              menu below carries it instead so it is never unreachable. */}
          {loggedIn && isAgent && (
            <button
              onClick={() => { setMenuOpen(false); navigate('/wallet'); }}
              title={agencyName ? `Corporate account · ${agencyName}` : 'Corporate account'}
              className="hidden lg:inline-flex shrink-0 max-w-[190px] items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#101820]/5 border border-[#101820]/20 text-[#101820] text-xs font-bold hover:bg-[#101820]/10 cursor-pointer"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
                <path fill="currentColor" d="M3 21V7l7-4v4l7-2v16h-5v-5h-4v5zm4-7h2v-2H7zm0-4h2V8H7zm0 8h2v-2H7zm6-4h2v-2h-2zm0-4h2V8h-2zm0 8h2v-2h-2z" />
              </svg>
              <span className="truncate">{agencyName || 'Corporate account'}</span>
            </button>
          )}
          {/* mobile menu toggle */}
          <button onClick={() => setMenuOpen((v) => !v)} className="md:hidden w-10 h-10 rounded-xl border border-[#101820]/25 text-[#101820] flex items-center justify-center cursor-pointer" aria-label="Menu">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" /></svg>
          </button>
        </div>
      </div>

      {/* mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0C1218] px-5 py-3 flex flex-col gap-1">
          {LINKS.map((l) => (
            <a key={l.key} onClick={() => goSearch(l.key)} className={'px-3 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ' + (activeKey === l.key ? 'bg-white text-[#101820]' : 'text-white/80 hover:bg-white/10')}>
              {l.label}
            </a>
          ))}

          {/* Corporate identity for every viewport BELOW lg — the badge in the bar
              above is `hidden lg:inline-flex`, and this menu exists under 768px,
              so between them the only gap is 768–1024px where the bar itself is
              too tight to carry it. Not inside the `sm:hidden` block below: the
              wallet and theme controls duplicate the bar's own, this does not. */}
          {loggedIn && isAgent && (
            <div className="mt-1 pt-2 border-t border-white/10 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">Corporate account</p>
              <p className="text-sm font-semibold text-white truncate">{agencyName || 'Your company'}</p>
            </div>
          )}

          {/* Wallet + theme live here on a PHONE. Both are `hidden sm:inline-flex`
              in the bar above — the row only fits the logo, the auth pill and this
              menu button below 640px — so without this they were unreachable on a
              phone entirely: no way to read your balance and no way to switch to
              dark mode. `sm:hidden` on the block is load-bearing: between 640px and
              the menu's own 768px breakpoint BOTH the bar and this dropdown exist,
              and without it the wallet and toggle would appear twice. */}
          <div className="sm:hidden mt-1 pt-2 border-t border-white/10 flex flex-col gap-1">
            {loggedIn && walletFeatureEnabled && (
              <a
                onClick={() => { setMenuOpen(false); navigate('/wallet'); }}
                className="px-3 py-2.5 rounded-lg text-sm font-semibold text-white/80 hover:bg-white/10 cursor-pointer flex items-center justify-between gap-3"
              >
                My wallet
                <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white text-xs font-bold">
                  {inr(walletBalance || 0)}
                </span>
              </a>
            )}
            <div className="px-3 py-2 flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-white/80">Dark mode</span>
              <ThemeToggle variant="sky" size="sm" showLabel={false} className="shrink-0" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
